// The in-flow pairing dialog — shippra's PhoneScanModal, rebuilt on the tab
// hub: a desk screen (Issue, Return, Add Book…) opens it to pair a phone
// WITHOUT leaving the flow, watches the live feed, and closes it — the pairing
// survives because the hub owns the link, not this dialog.
//
// Gate (c) note: the modal surfaces pairing + the feed only. What a scan DOES
// on the open screen is that screen's registered staging handler; nothing in
// here acts on a scan.
import { For, Show, createEffect, createSignal } from "solid-js";
import { isLoopbackHost, phoneScanUrl } from "./bridge.js";
import { useQuery } from "@tanstack/solid-query";
import { queryState } from "@erp/ui/route-kit";
import { libraryQueries } from "../queries.js";
import { Barcode } from "../cards/Barcode.jsx";
import { Button } from "@erp/ui/button";
import { CopyButton } from "@erp/ui/copy-button";
import { Modal } from "@erp/ui/modal";
import { QrCode } from "@erp/ui/qr-code";
import { ChevronDown, RefreshCw, ShieldCheck, Smartphone, Unplug, Wifi, WifiOff } from "lucide-solid";
import { ScanFeed } from "./ScanFeed.jsx";
import { useScanHub } from "./hub.js";

/** Warn only when the QR is genuinely unreachable: the desk is on a loopback
 * host AND the pairing URL could not be rewritten to a LAN address (dev
 * substitutes one — see bridge.pairingOrigin). */
const onLocalhost = (): boolean =>
  typeof window !== "undefined" &&
  isLoopbackHost() &&
  new URL(phoneScanUrl("probe")).hostname === window.location.hostname;

export function PhoneScanModal(props: {
  open: boolean;
  onClose: () => void;
  /** One line under the title saying what scans do on THIS screen. */
  contextHint?: string;
}) {
  const hub = useScanHub();

  // ── Test codes: REAL rows, fetched only when the tester expands the panel.
  // A viewer without the patrons/catalogue read simply sees empty sections —
  // the queries fail quietly (retry off) rather than blocking pairing.
  const [showTest, setShowTest] = createSignal(false);
  const testPatrons = queryState(useQuery(() => ({
    ...libraryQueries.patrons({ status: "active", limit: 3 }),
    enabled: props.open && showTest(),
    retry: false,
  })));
  const testBooks = queryState(useQuery(() => ({
    ...libraryQueries.books({ limit: 4, sort: "title:asc" }),
    enabled: props.open && showTest(),
    retry: false,
  })));

  // Lazy: the relay link opens the first time the dialog is opened.
  createEffect(() => {
    if (props.open) hub.start();
  });

  const disconnect = async (): Promise<void> => {
    await hub.removeAllDevices();
    hub.stop();
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Scan with a phone"
      subtitle={props.contextHint ?? "Pair a phone as a wireless scanner — scans land on this screen"}
      icon={Smartphone}
      width={780}
      id="phone-scan-modal"
    >
      <div class="grid min-w-0 grid-cols-1 gap-5 md:grid-cols-2">
        {/* ── Pairing ── */}
        <div class="flex min-w-0 flex-col items-center gap-3">
          <div class="rounded-500 border border-base-300 bg-base-100 p-3">
            <Show when={hub.pairingUrl()} fallback={<div class="h-[172px] w-[172px] max-w-full" />}>
              <QrCode value={hub.pairingUrl()} size={172} label="Pairing QR code" class="h-auto max-w-full" />
            </Show>
          </div>
          <div class="text-center">
            <div class="text-detail text-base-subtle">Pairing code</div>
            <div class="flex items-center justify-center gap-2">
              <span id="phone-scan-code" class="font-mono text-2xl font-bold tracking-[0.2em] tabular-nums sm:text-3xl sm:tracking-[0.3em]">
                {hub.code() || "······"}
              </span>
              <Button
                variant="ghost"
                size="sm"
                aria-label="New pairing code"
                onClick={() => void hub.regenerateCode()}
              >
                <RefreshCw size={14} aria-hidden="true" />
              </Button>
            </div>
          </div>
          <div class="flex w-full items-center gap-2 rounded-field border border-base-300 px-3 py-2">
            <span class="min-w-0 flex-1 truncate font-mono text-xs text-base-subtle">{hub.pairingUrl()}</span>
            {/* An accessor: the URL regenerates with the code, and the button copies the CURRENT one.
                Toast feedback — the URL on screen does not change to show the copy landed. */}
            <CopyButton id="btn-phone-scan-copy" text={hub.pairingUrl} label="Pairing link" feedback="toast" />
          </div>
          <div class="flex items-center gap-1.5 text-xs" id="phone-scan-status">
            <Show
              when={hub.devices().length > 0}
              fallback={
                <Show
                  when={hub.connected()}
                  fallback={
                    <>
                      <WifiOff size={13} class="text-base-subtle" aria-hidden="true" />
                      <span class="text-base-subtle">Connecting to the relay…</span>
                    </>
                  }
                >
                  <Wifi size={13} class="text-info" aria-hidden="true" />
                  <span class="text-base-subtle">Waiting for a phone to pair…</span>
                </Show>
              }
            >
              <ShieldCheck size={13} class="text-success" aria-hidden="true" />
              <span class="text-base-muted">
                Phone linked — scanning live ({hub.devices().length})
              </span>
              <Button id="btn-phone-scan-disconnect" variant="ghost" size="sm" class="text-error" onClick={() => void disconnect()}>
                <Unplug size={13} aria-hidden="true" />
                Disconnect
              </Button>
            </Show>
          </div>
          <Show when={onLocalhost()}>
            <p class="rounded-field bg-warning-tint p-2.5 text-xs text-base-muted">
              This portal is open on <span class="font-mono">localhost</span>, and no LAN address was detected — a
              phone cannot reach that. Open the portal via its network URL first, then pair.
            </p>
          </Show>
        </div>

        {/* ── Live feed ── */}
        <div class="min-w-0">
          <div class="mb-2 flex items-center justify-between">
            <h4 class="text-sm">Incoming scans</h4>
            <Show when={hub.recent().length > 0}>
              <Button variant="ghost" size="sm" onClick={() => hub.clearRecent()}>
                Clear
              </Button>
            </Show>
          </div>
          <ScanFeed
            events={hub.recent()}
            idPrefix="phone-scan-feed"
            emptyDescription="Pair a phone and scan — every code lands here and on this screen instantly."
          />
          <p class="mt-3 text-xs text-base-subtle">
            The pairing stays live while this tab is open — you can close this dialog and keep scanning.
          </p>

          {/* ── Test codes — real rows rendered as scannables ── */}
          <div class="mt-4 border-t border-base-300 pt-3">
            <button
              type="button"
              id="btn-phone-scan-test-codes"
              class="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              onClick={() => setShowTest((v) => !v)}
            >
              <ChevronDown size={13} class={showTest() ? "rotate-180 transition-transform" : "transition-transform"} aria-hidden="true" />
              Test codes — scan these from the screen
            </button>
            <Show when={showTest()}>
              <div class="mt-3 flex flex-col gap-3">
                <Show when={(testPatrons.data()?.data ?? []).filter((m) => m.cardNumber).length > 0}>
                  <div>
                    <div class="text-detail-s mb-1.5 text-base-subtle">
                      Patron cards
                    </div>
                    <div class="flex flex-wrap gap-3">
                      <For each={(testPatrons.data()?.data ?? []).filter((m) => m.cardNumber).slice(0, 3)}>
                        {(m) => (
                          <div class="flex flex-col items-center gap-1 rounded-field border border-base-300 p-2">
                            <QrCode value={m.cardNumber!} size={68} />
                            <span class="max-w-24 truncate text-xs text-base-subtle">{m.displayName}</span>
                          </div>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
                <Show when={(testBooks.data()?.data ?? []).length > 0}>
                  <div>
                    <div class="text-detail-s mb-1.5 text-base-subtle">
                      Book ISBNs
                    </div>
                    <div class="flex flex-col gap-2">
                      <For each={(testBooks.data()?.data ?? []).slice(0, 4)}>
                        {(b) => (
                          <div class="flex items-center gap-3 rounded-field border border-base-300 p-2 text-base-content">
                            <Barcode value={b.isbn ?? ""} height={26} moduleWidth={1.2} />
                            <span class="min-w-0 truncate text-xs text-base-subtle">{b.title}</span>
                          </div>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
                <Show when={(testPatrons.data()?.data ?? []).length === 0 && (testBooks.data()?.data ?? []).length === 0}>
                  <p class="text-xs text-base-subtle">
                    Nothing to show — this account may lack the patrons/catalogue read, or the registries are empty.
                  </p>
                </Show>
                <p class="text-xs text-base-subtle">
                  Tip: the phone's Upload button reads several codes from one photo.
                </p>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Modal>
  );
}
